<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Show Invoice
            </h3>

            <p>
              On this page, you can see the details of the invoice.
            </p>

            <div class="btn-group">
              <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-cogs"></i> Actions <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li :class="{disabled: !canDraft}">
                  <a href="#" @click.prevent="draft">
                    <i class="fa fa-share"></i> Draft
                  </a>
                </li>

                <li :class="{disabled: !canBook}">
                  <a href="#" @click.prevent="book">
                    <i class="fa fa-paper-plane"></i> Book
                  </a>
                </li>

                <li :class="{disabled: !canMarkAsPaid}">
                  <a href="#" @click.prevent="markAsPaid">
                    <i class="fa fa-check"></i> Mark as paid
                  </a>
                </li>

                <li :class="{disabled: !canUnpay}">
                  <a href="#" @click.prevent="unpay">
                    <i class="fa fa-times"></i> Mark as unpaid
                  </a>
                </li>

                <li :class="{disabled: !canCredit}">
                  <a href="#" @click.prevent="credit">
                    <i class="fa fa-hand-holding-usd"></i> Credit
                  </a>
                </li>

                <li role="separator" class="divider"></li>

                <li :class="{disabled: !canDestroy}">
                  <a href="#" @click.prevent="destroy">
                    <i class="fa fa-trash"></i> Delete
                  </a>
                </li>
              </ul>
            </div>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body form-horizontal">
              <static-form-group
                  label="Accounting System Status"
                  :value="status"
              ></static-form-group>

              <static-form-group
                  label="Accounting System Number"
                  :value="invoice.attributes.accounting_system_number"
              ></static-form-group>

              <static-form-group
                  label="Date"
                  :value="invoice.attributes.date | date"
              ></static-form-group>

              <static-form-group
                  label="Due At"
                  :value="invoice.attributes.due_at | date"
              ></static-form-group>

              <static-form-group
                  label="First Notice Scheduled For"
                  :value="invoice.attributes.first_notice_scheduled_for | date"
              ></static-form-group>

              <static-form-group
                  label="Second Notice Scheduled For"
                  :value="invoice.attributes.second_notice_scheduled_for | date"
              ></static-form-group>

              <static-form-group
                  label="Third Notice Scheduled For"
                  :value="invoice.attributes.third_notice_scheduled_for | date"
              ></static-form-group>

              <static-form-group
                  label="Paid At"
                  :value="invoice.attributes.paid_at | date"
              ></static-form-group>

              <static-form-group
                  label="Credited At"
                  :value="invoice.attributes.credited_at | date"
              ></static-form-group>

              <static-form-group
                  label="First Notice Sent At"
                  :value="invoice.attributes.first_notice_sent_at | date"
              ></static-form-group>

              <static-form-group
                  label="Second Notice Sent At"
                  :value="invoice.attributes.second_notice_sent_at | date"
              ></static-form-group>

              <static-form-group
                  label="Third Notice Sent At"
                  :value="invoice.attributes.third_notice_sent_at | date"
              ></static-form-group>
            </div>
          </div>
        </div>
      </div>

      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Invoice Lines
            </h3>

            <p>
              This table displays the content of the invoice.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body form-horizontal">
              <table class="table table-condensed table-striped">
                <thead>
                <tr>
                  <th class="col-xs-2 text-center">
                    <input type="checkbox" :disabled="true">
                  </th>

                  <th class="col-xs-10">
                    Product
                  </th>

                  <th class="col-xs-4">
                    Unit Price
                  </th>

                  <th class="col-xs-4">
                    Portal
                  </th>

                  <th class="col-xs-4"></th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="invoiceLine in invoice.attributes.invoice_lines">
                  <td class="text-center">
                    <input type="checkbox" :disabled="true">
                  </td>

                  <td>
                                            <span
                                                v-tooltip="getBookkeepingProduct(invoiceLine)"
                                                style="text-decoration: underline; text-decoration-style: dotted;"
                                            >
                                                {{ invoiceLine.quantity }}x {{ invoiceLine.description }}
                                            </span>
                  </td>

                  <td>
                    {{ invoiceLine.unit_price | price }}

                    <i
                        v-if="hasAccrual(invoiceLine)"
                        class="fa fa-clock-o"
                        v-tooltip="getAccrual(invoiceLine)"
                    ></i>
                  </td>

                  <td>
                    <router-link :to="'/portals/' + invoiceLine.portal_id">
                      {{ invoiceLine.portal_name }}
                    </router-link>
                  </td>

                  <td class="text-right">
                    <div class="btn-group list-actions">
                      <button class="btn btn-sm" :disabled="true">
                        <i class="fa fa-search"></i>
                      </button>

                      <button class="btn btn-sm" :disabled="true">
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!invoice.attributes.invoice_lines.length">
                  <td colspan="6" class="text-center">
                    <i>
                      There are no invoice lines to display.
                    </i>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <book-invoice-modal :invoice="invoice" @booked="invoice = $event"></book-invoice-modal>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import notify from '@/notify'
import BookInvoiceModal from '@/pages/Shared/Modals/BookInvoice'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import Permissions from '@/mixins/Permissions'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'

export default {
  components: {BookInvoiceModal, HelpText, LoadingContainer, StaticFormGroup},

  mixins: [Errors, Permissions],

  model: {
    prop: 'owner',
    event: 'owner-updated'
  },

  props: ['owner', 'invoiceId'],

  data() {
    return {
      invoice: null,
      bookkeepingProducts: [],

      loading: true,
    }
  },

  mounted() {
    this.load()
  },

  watch: {
    '$route'() {
      this.load()
    }
  },

  computed: {
    canDraft() {
      return this.hasPermission('admin') && this.invoice.attributes.accounting_system_status === null
    },

    canBook() {
      return this.hasPermission('admin') && this.invoice.attributes.accounting_system_status !== 'booked'
    },

    canMarkAsPaid() {
      return this.hasPermission('admin') && !this.invoice.attributes.paid_at
    },

    canUnpay() {
      return this.hasPermission('admin') && this.invoice.attributes.paid_at
    },

    canCredit() {
      return this.hasPermission('admin') && !this.invoice.attributes.credited_at
    },

    canDestroy() {
      return this.hasPermission('admin') && this.invoice.attributes.accounting_system_status !== 'booked'
    },

    status() {
      if (this.invoice.attributes.accounting_system_status === 'drafted') {
        return 'Drafted'
      }

      if (this.invoice.attributes.accounting_system_status === 'booked') {
        return 'Booked'
      }
    }
  },

  methods: {
    load() {
      Promise.all([
        http.get('/api/v2/invoices/' + this.invoiceId),
        http.get('/api/v2/bookkeeping-products')
      ]).then(responses => {
        this.invoice = responses[0].data.data

        this.bookkeepingProducts = responses[1].data.data

        this.loading = false
      })
    },

    hasAccrual(invoiceLine) {
      return invoiceLine.accrual_starts_at || invoiceLine.accrual_ends_at
    },

    getAccrual(invoiceLine) {
      return (
          this.$options.filters.date(invoiceLine.accrual_starts_at) +
          ' - ' +
          this.$options.filters.date(invoiceLine.accrual_ends_at)
      )
    },

    getBookkeepingProduct(invoiceLine) {
      let bookkeepingProduct = this.bookkeepingProducts.find(
          product => product.id.toString() === invoiceLine.accounting_system_product_number.toString()
      )

      return bookkeepingProduct ? bookkeepingProduct.attributes.name : '(unknown)'
    },

    credit() {
      if (!this.canCredit) {
        return
      }

      modal.confirm('Are you sure that you want to credit the invoice?', () => {
        http.post('/api/v2/invoices/' + this.invoice.id + '/credit')
            .then(response => {
              notify.success('The invoice was credited')

              this.$router.push('./' + response.data.data.id)
            })
      })
    },

    draft() {
      if (!this.canDraft) {
        return
      }

      http.post('/api/v2/invoices/' + this.invoice.id + '/draft')
          .then(response => {
            notify.success('The invoice was drafted')

            this.invoice = response.data.data
          })
    },

    book() {
      if (!this.canBook) {
        return
      }

      $('#book-invoice-modal').modal('show')
    },

    markAsPaid() {
      if (!this.canMarkAsPaid) {
        return
      }

      modal.confirm('Are you sure that you want to mark the invoice as paid?', () => {
        http.post('/api/v2/invoices/' + this.invoice.id + '/pay')
            .then(response => {
              notify.success('The invoice was marked as paid')

              this.invoice = response.data.data
            })
      })
    },

    unpay() {
      if (!this.canUnpay) {
        return
      }

      modal.confirm('Are you sure that you want to mark the invoice as unpaid?', () => {
        http.post('/api/v2/invoices/' + this.invoice.id + '/unpay')
            .then(response => {
              notify.success('The invoice was marked as unpaid')

              this.invoice = response.data.data
            })
      })
    },

    destroy() {
      if (!this.canDestroy) {
        return
      }

      modal.confirm('Are you sure that you want to delete the invoice?', () => {
        http.delete('/api/v2/invoices/' + this.invoice.id)
            .then(() => {
              notify.success('The invoice was deleted')

              this.$router.push('..')
            })
      })
    }
  }
}
</script>
